<template>
  <div
    v-if="isTextMode"
    class="base-select__text-mode">
    <span>
      {{ placeholder }}
    </span>
    <span class="base-select__text-mode-gray">
      {{ selectedItems[0] ? selectedItems[0][titleField] : value }}
    </span>
  </div>
  <div v-else-if="listView">
    <slot
      name="list"
      :select-item="selectItem"
      :available-items="availableItems"
      :selected-items="selectedItems"
      :selected-indexes="selectedIndexes" />
  </div>
  <div
    v-else
    class="base-select__wrapper">
    <div
      v-if="isOpen"
      class="base-select__overlay"
      @click="closeItems" />

    <div
      ref="select"
      class="base-select"
      :class="{
        'base-select--open': isOpen,
      }"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave">
      <div
        class="base-select__value"
        @click="handleClick">
        <slot
          name="value"
          :select-item="selectItem"
          :selected-items="selectedItems" />
      </div>
      <div
        class="base-select__items">
        <div
          ref="items"
          class="base-select__items-wrapper">
          <slot
            name="options"
            :select-item="selectItem"
            :close-items="closeItems"
            :available-items="availableItems"
            :selected-items="selectedItems"
            :selected-indexes="selectedIndexes"
            :is-overflowed="isOverflowed" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'BaseSelectNew',

  props: {
    items: {
      type: Array,
      required: true,
    },

    value: {
      type: [String, Number, Array, Object],
      default: () => null,
    },

    valueField: {
      type: String,
      default: null,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    canHaveEmptyValue: {
      type: Boolean,
      default: false,
    },

    canUnselect: {
      type: Boolean,
      default: true,
    },

    shouldHideSelectedValue: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    openTriggers: {
      type: Array,
      default: () => ['click'],
    },

    titleField: {
      type: String,
      default: 'title',
    },

    isTextMode: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: String,
      default: null,
    },

    listView: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: false,
      selectedIndexes: [],
      isOverflowed: false,
    };
  },

  computed: {
    ...mapState(['userDevice']),

    isItemObject() {
      return typeof this.items[0] === 'object';
    },

    selectedItems() {
      return this.items.filter((item, index) => this.selectedIndexes.includes(index));
    },

    availableItems() {
      if (!this.multiple && this.shouldHideSelectedValue && this.value) {
        return this.items.filter((item, index) => index !== this.selectedIndexes[0]);
      }
      return this.items;
    },

    innerValue() {
      const itemsArray = this.items.filter((item, index) => this.selectedIndexes.includes(index));
      const valueArray = this.isItemObject && this.valueField
        ? itemsArray.map((item) => item[this.valueField])
        : itemsArray;

      return this.multiple
        ? valueArray
        : valueArray[0] ?? null;
    },
  },

  watch: {
    value: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.setInitialValue();
        }
      },
    },
  },

  created() {
    this.setInitialValue();
  },

  methods: {
    handleClick() {
      if (!this.openTriggers.includes('click')) {
        return;
      }

      this.toggleItems();
    },

    handleMouseEnter() {
      if (!this.openTriggers.includes('hover') || (this.openTriggers.includes('click') && this.userDevice.isCoarse)) {
        return;
      }

      this.openItems();
    },

    handleMouseLeave() {
      if (!this.openTriggers.includes('hover') || (this.openTriggers.includes('click') && this.userDevice.isCoarse)) {
        return;
      }

      this.closeItems();
    },

    openItems() {
      this.isOpen = true;
      const selectRect = this.$refs.select.getBoundingClientRect();
      const optionsRect = this.$refs.items.getBoundingClientRect();
      this.isOverflowed = selectRect.bottom + optionsRect.height > window.innerHeight;

      this.$emit('open');
    },

    closeItems() {
      this.isOpen = false;
      this.$emit('close');
    },

    toggleItems() {
      if (this.disabled) {
        return;
      }

      if (this.isOpen) {
        this.closeItems();
      } else {
        this.openItems();
      }
    },

    findItemIndex(item) {
      if (!item) {
        return null;
      }
      return this.items.findIndex((val) => {
        if (item && this.isItemObject) {
          return this.valueField
            ? item[this.valueField] === val[this.valueField]
            : JSON.stringify(item) === JSON.stringify(val);
        }
        return item === val;
      });
    },

    selectItem(item) {
      // Эта штука нужна для того что бы не ставить значение в инпут при желании
      // Если в обьект из массива items указать функцию "beforeSelect", можно контролировать поведение
      if (item.beforeSelect) {
        /*
          Например при выборе какого то пункта, нужно выполнить логику,
          и в зависимости от чего то запрещать ставить значение
          Обязательно нужно возвращать TRUE если хочешь, что бы значение поставилось
         */
        const canSelect = item.beforeSelect(item[this.valueField] || item);

        if (!canSelect) {
          return;
        }
      }

      const itemIndex = this.findItemIndex(item);

      const remove = this.selectedIndexes.includes(itemIndex);
      if (this.multiple) {
        if (remove) {
          // Король королей
          const indexOfIndex = this.selectedIndexes.findIndex((i) => i === itemIndex);
          this.selectedIndexes.splice(indexOfIndex, 1);
        } else {
          this.selectedIndexes.push(itemIndex);
        }
      } else {
        if (remove) {
          if (this.canHaveEmptyValue && this.canUnselect) {
            this.selectedIndexes = [];
          }
        } else {
          if (this.selectedIndexes.length) {
            this.selectedIndexes = [];
          }
          this.selectedIndexes.push(itemIndex);
        }
        this.closeItems();
      }

      this.emitValue();
    },

    emitValue() {
      this.$emit('input', this.innerValue);
    },

    setInitialValue() {
      this.selectedIndexes = [];
      if (this.multiple) {
        if (!Array.isArray(this.value)) {
          return;
        }
        this.value.forEach((item) => {
          const currentItem = this.valueField
            ? this.items.find((i) => i[this.valueField] === item)
            : item;
          const itemIndex = this.findItemIndex(currentItem);
          this.selectedIndexes.push(itemIndex);
        });
      } else {
        let shouldEmit = false;
        const currentItem = this.valueField
          ? this.items.find((i) => i[this.valueField] === this.value)
          : this.value;
        let itemIndex = this.findItemIndex(currentItem);

        if (itemIndex === -1 && !this.canHaveEmptyValue) {
          itemIndex = 0;
          shouldEmit = true;
        }

        if (itemIndex > -1) {
          this.selectedIndexes.push(itemIndex);
        }

        if (shouldEmit) {
          this.emitValue();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .base-select__text-mode.base-select__text-mode {
    display: flex;
    align-items: center;
    font-size: 15px;
    font-weight: 500;
  }

  .base-select__text-mode-gray {
    color: $color--basic-dark-gray;
    margin-left: 10px;
  }

  .base-select__wrapper {
    --z-index: 3;

    font-family: 'Rubik', sans-serif;
  }

  .base-select__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: calc(var(--z-index) - 1);
  }

  .base-select {
    position: relative;
    z-index: var(--z-index);
  }

  .base-select--open {
    .base-select__items {
      visibility: visible;
      opacity: 1;
    }
  }

  .base-select__items {
    visibility: hidden;
    opacity: 0;
    height: 0;
    position: relative;
    transition: var(--transition-color-time) var(--transition-color-easing);
    transition-property: background-color, opacity;
  }

  .base-select__items-wrapper {
    width: 100%;
  }
</style>
